import Modal from '@/components/common/Modal'
import { PremiumText } from '@/components/Premium/PremiumStyled'
import ButtonIcon from '@/components/common/ButtonIcon'
import styled from 'styled-components'
import Row from '@/components/common/Row'
import Button from '@/components/ui/Button'
import BaseTextArea from '@/components/common/BaseTextArea'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useRouter } from 'next/router'
import { close } from '@/store/modalsSlice'
import { device } from '@/common/constants/device'
import Input from '@/components/ui/Input'
import { useForm } from 'react-hook-form'
import { emailRule, requiredRule } from '@/common/validationRules'
import normalizeInput from '@/hooks/useTrim'
import { tariffApi } from '@/common/services/api'
import { useActions } from '@/hooks/useActions'
import Select from 'react-select'
import { useTranslations } from 'next-intl'

const PremiumEnterpriseModalBlock = styled.div`
  padding: 0 16px;

  @media ${device.tablet} {
    padding: 0 24px;
  }

  .premium-modal-email {
    margin-bottom: 16px;
  }
`
const PremiumEnterpriseModalContent = styled.div`
  position: relative;
  padding: 20px 0;
  display: flex;
  flex-direction: column;
  row-gap: 16px;
  width: 100%;
`

const PremiumEnterpriseModalWrap = styled(Modal)`
  .modal-content {
    width: calc(100% - 30px);

    @media ${device.tablet} {
      width: 100%;
    }
  }
`

const PremiumEnterpriseModalTop = styled(Row)`
  justify-content: space-between;
  column-gap: 12px;
  padding: 0 16px;

  @media ${device.tablet} {
    padding: 0 24px;
    align-items: center;
  }
`

const PremiumEnterpriseModalTitle = styled(PremiumText)`
  color: var(--black-russian);
`

const PremiumEnterpriseModalClose = styled(ButtonIcon)`
  width: 24px;
  height: 24px;
  cursor: pointer;
  background-color: var(--tertiary);
  transition: transform 0.26s ease-in-out;
  flex-shrink: 0;

  &:hover {
    transform: rotate(90deg);
  }

  @media ${device.tablet} {
    width: 32px;
    height: 32px;
  }
`

const PremiumEnterpriseModalRow = styled.div`
  display: grid;
  grid-gap: 8px 12px;
  grid-template-columns: 1fr;
`

const PremiumEnterpriseModalEmailText = styled.div`
  font-size: 14px;
  line-height: 1.2em;

  @media ${device.tablet} {
    font-size: 16px;
    line-height: 1.2em;
  }
`

const PremiumSelect = styled(Select)`
  width: 100%;
`

const PremiumEnterpriseModalButton = styled(Button)`
  width: 100%;
`

const PremiumEnterpriseModal = () => {
  const [isLoading, setLoading] = useState(false)
  const { user } = useSelector((state) => state.user)
  const t = useTranslations('common')
  const dispatch = useDispatch()
  const { addNotification } = useActions()
  const [selectedOption, setSelectedOption] = useState(null)
  const { locale } = useRouter()

  const selectedFields = useMemo(() => {
    return [
      { name: 'partners_marketing', label: t('partnersMarketing') },
      { name: 'dropshiping', label: t('dropshiping') },
      { name: 'small_business', label: t('smallBusiness') },
      { name: 'Medium business', label: t('mediumBusiness') },
      { name: 'big_business', label: t('bigBusiness') },
      { name: 'advertising_agency', label: t('advertisingAgency') },
      { name: 'fcmg', label: t('fcmg') },
      { name: 'horeca', label: t('horeca') },
      { name: 'service', label: t('service') },
      {
        name: 'developers_and_marketing',
        label: t('developersMarketing'),
      },
      { name: 'freelancing', label: t('freelancing') },
    ]
  }, [locale])

  const {
    register,
    setValue,
    watch,
    clearErrors,
    trigger,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: '',
      email: '',
      telegram: '',
      message: '',
    },
  })

  const { email, name, telegram, message } = watch()

  useEffect(() => {
    register('email', { required: requiredRule, pattern: emailRule })
    register('name', { required: requiredRule })
  }, [register])

  useEffect(() => {
    const userEmail = user?.email ?? ''
    const userName = user?.name ?? ''

    setValue('name', userName)
    setValue('email', userEmail)
  }, [user])

  const closeModal = () => {
    dispatch(close())
  }

  const onClickButtonHandler = async () => {
    try {
      const isValid = await trigger()
      if (!isValid) return

      setLoading(true)

      await tariffApi.createEnterprise({
        data: {
          name,
          email,
          telegram,
          message,
          activity: selectedOption?.label ?? '',
        },
      })

      addNotification({
        type: 'success',
        text: 'common.success',
      })

      closeModal()
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  const handleTypeSelect = (e) => {
    setSelectedOption(e)
  }

  return (
    <PremiumEnterpriseModalWrap maxWidth="644" needOffsetHeader>
      <PremiumEnterpriseModalContent>
        <PremiumEnterpriseModalTop>
          <PremiumEnterpriseModalTitle>
            {t('enterpriseTariff')}
          </PremiumEnterpriseModalTitle>
          <PremiumEnterpriseModalClose onClick={closeModal} icon="cross" />
        </PremiumEnterpriseModalTop>
        <PremiumEnterpriseModalBlock>
          <PremiumEnterpriseModalRow className="premium-modal-email">
            <PremiumEnterpriseModalEmailText>
              {t('modalInputNameLabel')} *
            </PremiumEnterpriseModalEmailText>
            <Input
              placeholderColor="white"
              id="name"
              type="text"
              value={name}
              placeholder="Name"
              tooltipMessage={errors?.name?.message}
              isError={Boolean(errors?.name?.message)}
              onChange={(e) => setValue('name', normalizeInput(e.target.value))}
              onClick={() => clearErrors('name')}
            />
          </PremiumEnterpriseModalRow>
          <PremiumEnterpriseModalRow className="premium-modal-email">
            <PremiumEnterpriseModalEmailText>
              {t('modalInputEmailLabel')} *
            </PremiumEnterpriseModalEmailText>
            <Input
              placeholderColor="white"
              id="email"
              type="text"
              value={email}
              placeholder="Email"
              tooltipMessage={errors?.email?.message}
              isError={Boolean(errors?.email?.message)}
              onChange={(e) =>
                setValue('email', normalizeInput(e.target.value))
              }
              onClick={() => clearErrors('email')}
            />
          </PremiumEnterpriseModalRow>
          <PremiumEnterpriseModalRow className="premium-modal-email">
            <PremiumEnterpriseModalEmailText>
              {t('modalInputTelegramLabel')}
            </PremiumEnterpriseModalEmailText>
            <Input
              placeholderColor="white"
              id="telegram"
              type="text"
              value={telegram}
              placeholder="Telegram"
              tooltipMessage={errors?.telegram?.message}
              isError={Boolean(errors?.telegram?.message)}
              onChange={(e) =>
                setValue('telegram', normalizeInput(e.target.value))
              }
              onClick={() => clearErrors('telegram')}
            />
          </PremiumEnterpriseModalRow>
          <PremiumEnterpriseModalRow className="premium-modal-email">
            <PremiumEnterpriseModalEmailText>
              {t('modalInputActivityLabel')}
            </PremiumEnterpriseModalEmailText>
            <PremiumSelect
              value={selectedOption}
              onChange={handleTypeSelect}
              options={selectedFields}
              menuPosition="fixed"
              placeholder={t('modalActivityPlaceholder')}
              className="premiumSelect"
              styles={{
                singleValue: (provided) => ({
                  ...provided,
                  'grid-area': '1/1',
                }),
                option: (base) => {
                  return {
                    ...base,
                    padding: '16px 20px 16px',
                    backgroundColor: 'white',
                    color: 'dark',
                    ':hover': {
                      backgroundColor: 'var(--divider)',
                    },
                    ':active': {
                      backgroundColor: 'red',
                    },
                  }
                },
                control: (baseStyles) => ({
                  ...baseStyles,
                  borderColor: 'var(--divider)',
                  boxShadow: 'none',
                  '&:hover': {
                    border: 'none',
                  },
                  height: '53px',
                  ':hover': {
                    boxShadow: '0px 0px 0px 2px #d4dced',
                    borderColor: '#d4dced',
                  },
                  ':active': {
                    borderColor: 'ThreeDFace',
                  },
                  'padding-left': '12px',
                }),
              }}
              isSearchable={false}
            />
          </PremiumEnterpriseModalRow>
          <PremiumEnterpriseModalRow className="mb-4">
            <PremiumEnterpriseModalEmailText>
              {t('enterMessage')}
            </PremiumEnterpriseModalEmailText>
            <BaseTextArea
              name="messageArea"
              cols="40"
              rows="5"
              value={message}
              onChange={(e) => setValue('message', e.target.value)}
              onBlur={(e) =>
                setValue('message', normalizeInput(e.target.value))
              }
            ></BaseTextArea>
          </PremiumEnterpriseModalRow>
          <PremiumEnterpriseModalButton
            type="button"
            onClick={onClickButtonHandler}
            isLoading={isLoading}
            filled
          >
            {t('sendRequest')}
          </PremiumEnterpriseModalButton>
        </PremiumEnterpriseModalBlock>
      </PremiumEnterpriseModalContent>
    </PremiumEnterpriseModalWrap>
  )
}

export default PremiumEnterpriseModal
